import type { CostoFamilia, CostoHecho, CostoSavePayload, CostoSube, CostosUnificado } from "../../types";
import type { CostoPrefill } from "./CostoForm";
import { mesAnterior, mesPrevio } from "./costosFormat";

export type Filtro = CostoFamilia | "todas";

/** Datos y cálculos que comparten las vistas de escritorio y teléfono; el marcado no se comparte. */
export interface ModeloMes {
  mes: string;
  esActual: boolean;
  previo: string | null;
  total: number;
  gcpIncompleto: boolean;
  /** null cuando el mes o su anterior no tienen GCP completo. */
  deltaPct: number | null;
  hayManual: boolean;
  suben: CostoSube[];
}

export function modeloMes(data: CostosUnificado, mes: string): ModeloMes {
  const t = data.totales[mes];
  const previo = mesAnterior(mes, data.meses);
  const tPrevio = previo ? data.totales[previo] : null;
  const gcpIncompleto = !!t && (t.parcial_gcp || t.gcp_sin_datos);
  const comparable = !!tPrevio && !gcpIncompleto && !tPrevio.parcial_gcp && !tPrevio.gcp_sin_datos && tPrevio.total > 0;
  return {
    mes,
    esActual: mes === data.actual,
    previo,
    total: t?.total ?? 0,
    gcpIncompleto,
    deltaPct: comparable ? ((t.total - tPrevio!.total) / tPrevio!.total) * 100 : null,
    hayManual: data.hechos.some((h) => h.mes === mes && h.fuente === "manual" && h.familia === "manual"),
    suben: data.suben.filter((s) => s.delta > 0).slice(0, 6),
  };
}

/** Meses desde el primero con algún costo, para no mostrar un tramo inicial vacío. */
export function mesesVisibles(data: CostosUnificado): string[] {
  const i = data.meses.findIndex((m) => (data.totales[m]?.total ?? 0) !== 0);
  return i <= 0 ? data.meses : data.meses.slice(i);
}

export function filasDelMes(data: CostosUnificado, mes: string, filtro: Filtro): CostoHecho[] {
  return data.hechos
    .filter((h) => h.mes === mes && (filtro === "todas" || h.familia === filtro))
    .sort((a, b) => (b.monto_clp ?? -1) - (a.monto_clp ?? -1));
}

/** Datos de un costo manual para abrir su formulario de edición. */
export const prefillDe = (h: CostoHecho): CostoPrefill => ({
  id: h.id, categoria: h.categoria, concepto: h.servicio, monto: h.monto_clp ?? undefined,
  periodico: h.recurrente, mesOrigen: h.mes_origen, hasta: h.hasta ?? undefined,
});

/** Un costo recurrente se guarda en su mes de origen; «terminar» lo cierra en el mes anterior al que se está viendo. */
export function payloadCosto(a: {
  mes: string; prefill: CostoPrefill; categoria: string; concepto: string; montoClp: number; nota?: string; periodico: boolean; terminar?: boolean;
}): CostoSavePayload {
  return {
    id: a.prefill.id, month: a.prefill.mesOrigen ?? a.mes, categoria: a.categoria, concepto: a.concepto.trim(), monto_clp: a.montoClp, nota: a.nota,
    periodico: a.periodico, hasta: a.periodico ? (a.terminar ? mesPrevio(a.mes) : a.prefill.hasta) : undefined,
  };
}

export function etiquetas(h: CostoHecho): string[] {
  const out = [h.fuente === "export" ? "Exportación" : h.fuente === "manual" ? (h.recurrente ? "Manual · recurrente" : "Manual") : "Estimado por tokens"];
  if (!h.en_total) out.push("Ya incluido en GCP");
  if (h.parcial) out.push("Mes parcial");
  return out;
}

/** Texto de estado de GCP para el mes: lo que se ve cuando no hay cifra completa. */
export function estadoGcp(data: CostosUnificado, mes: string): string {
  const t = data.totales[mes];
  if (t?.gcp_sin_datos) return `La exportación llega al ${data.gcp_hasta ?? "—"}`;
  if (t?.parcial_gcp) return `Parcial: exportación del ${data.gcp_desde} al ${data.gcp_hasta}`;
  return "Exportación de facturación";
}
